<template>
  <div>
    <GuideVideoBanner
      v-if="props.guideData"
      :text="props.guideData.content.text"
      :subtext="props.guideData.content.subText"
      :video-url="props.guideData.content.videoUrl"
      :video-thumbnail="props.guideData.content.videoThumbnail"
      @close="handleGuideDismiss"
    />
    <div class="org-role-tab">
      <OrganizationRolesTable
        :dataStream="rolesData"
        :pagination="paginationModel"
        :chatterData="chatterData"
        :totalChatterData="totalChatterData"
        :useNewBadge="props.useNewBadge"
        :initialFilters="currentFilters"
        :showIndicators="true"
        :indicatorCounts="roleIndicators"
        :showAttachToPositionsBulk="true"
        :detachLoader="detachLoader"
        :loading="isLoading"
        @onPageChange="handlePageChange"
        @onFilterChange="handleFilterChange"
        @onSearchChange="handleSearchChange"
        @detach="handleDetach"
        @openChatterDrawer="handleOpenChatterDrawer"
        @requestMoreCheckpointsChatter="handleRequestMoreChatter"
        @onRowOperation="handleRowOperation"
        @onRoleDrillDown="handleRoleDrillDown"
      />
    </div>

    <!-- Dialogs -->
    <ActivateRoleDialog
      :value="showActivateRoleDialog"
      @input="showActivateRoleDialog = $event"
      @onSave="onActivateRoleConfirm"
      :name="currentRole?.role_name || currentRole?.data?.name || ''"
      :employee-count="activationEmployeeCount"
    />
    <ActivateResponsibilityConfirmationModal
      :dialog="showActivateResponsibility"
      @close="showActivateResponsibility = false"
      @skip="skipActivateResponsibility"
      @activate="activateAttachedResponsibilities"
      :role="currentRole?.role_name || currentRole?.data?.name || ''"
      :draftResponsibilityCount="draftResponsibilityCount"
      :skipButtonLoader="skipActivateButtonLoader"
      :activateButtonLoader="activateAttachedButtonLoader"
    />
    <RetireRoleDialog
      :show="showRetireRoleDialog"
      @onClose="showRetireRoleDialog = false"
      @onRetire="retireRole"
      :name="currentRole?.role_name || currentRole?.data?.name || ''"
      :role="currentRole"
    />
    <ReactivateRoleDialog
      :show="showReActivateRoleDialog"
      @onClose="showReActivateRoleDialog = false"
      @onReactivate="reActivateRole"
      :name="currentRole?.role_name || currentRole?.data?.name || ''"
      :role="currentRole"
      :employee-count="activationEmployeeCount"
    />
    <RenameRoleDialog
      v-if="showRenameRoleDialog && renameRoleModel"
      v-model="showRenameRoleDialog"
      :role="renameRoleModel"
      @saved="onRoleRenamed"
    />
    <DeleteRoleDialog
      :show="showDeleteRoleDialog"
      @onClose="showDeleteRoleDialog = false"
      @onDelete="deleteRole"
      :name="currentRole?.role_name || currentRole?.data?.name || ''"
    />
    <ArchivePopUp
      :action="archiveAction"
      type="role"
      :show="showArchiveDialog"
      :curretObj="currentRole"
      :errorMessage="unarchiveError"
      @onClose="onArchiveDialogClose"
      @onArchive="onArchiveRole"
    />
    <UnarchiveNameConflictDialog
      :show="unarchiveConflictOpen"
      objectType="role"
      :currentName="unarchiveTarget?.name || ''"
      :objectId="unarchiveTarget?.id || ''"
      :accountId="accountId"
      :busy="unarchiveBusy"
      :errorMessage="unarchiveError"
      :nameError="unarchiveNameError"
      @confirm="unarchiveRole.submitRename"
      @close="onArchiveDialogClose"
      @clear-error="unarchiveRole.clearErrors"
    />
    <AttachedResponsibility
      v-model="showAttachResponsibilityDialog"
      @onSave="handleAttachResponsibilityToRole"
      :items="attachableResponsibilities"
      @item-selected="handleResponsibilitySelected"
      @add-item-selected="handleAddNewResponsibility"
      :parentName="currentRole?.role_name || currentRole?.data?.name || ''"
      :openDefault="openDefaultAttachDialog"
      @input="closeAttachResponsibilityDialog"
      :type="
        showAttachResponsibilitiesPicker
          ? 'attach_responsibility_role_new_picker'
          : 'attach_responsibility_role'
      "
      @load-more="loadMoreResponsibilities"
      @search="searchResponsibilities"
      :hasMoreData="hasMoreResponsibilitiesData"
      :isLoadingMore="isLoadingMoreResponsibilities"
      :isAssigning="isAssigningResponsibility"
      :attach-target="attachTargetRole"
      @created-and-attached="handleResponsibilityCreatedAndAttached"
    />
    <AttachRoleToPositionsInBulkDialog
      v-model="showAttachRoleToPositionsBulkDialog"
      :role-name="currentRole?.role_name || currentRole?.data?.name || ''"
      action-type="attach"
      :positions="bulkAttachPositions"
      :loading="isBulkAttachPositionsLoading"
      :total="bulkAttachTotalPositions"
      :per-page="10"
      @onRequest="handleBulkAttachPositionRequest"
      @onSubmit="handleBulkAttachRoleToPositions"
      @onAddPosition="onBulkAttachAddPosition"
      @close="closeAttachRoleToPositionsBulkDialog"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance, computed } from "vue";
import { useRouter } from "vue-router/composables";
import {
  getListOfRolesV2,
  detachOrgObject,
  getRoleChatter,
  updateActiveRole,
  updateRetiredRole,
  updateReActiveRole,
  deleteReadinessRole,
  deleteReadinessRoleWithResponsibilities,
  updateArchiveRole,
  fetchAttachableResponsibilities,
  attachResponsibilityToRole,
  getChatterList,
  getListOfPositionsV2,
  getListOfPositionsAttachedToRole,
  attachRoleToPosition,
} from "@/api";
// Convert frontend filters to API format (stable snake_case values + legacy labels)
import { convertRoleFiltersToApiFormat } from "@components/FilterRolesModal/roleFilterPayload";
import { getDeleteErrorMessage } from "@components/utils/getDeleteErrorMessage";
import { fetchRoleActivationCount } from "@/utils/activationEmployeeCount";
import {
  createLatestRequestGuard,
  stableKey,
} from "@components/utils/createLatestRequestGuard";

import OrganizationRolesTable from "@components/OrganizationRolesTable/OrganizationRolesTable.vue";
import ActivateRoleDialog from "@components/ActivateRoleDialog/ActivateRoleDialog.vue";
import ActivateResponsibilityConfirmationModal from "@components/ActivateResponsibilityConfirmationModal/ActivateResponsibilityConfirmationModal.vue";
import RetireRoleDialog from "@components/RetireRoleDialog/RetireRoleDialog.vue";
import ReactivateRoleDialog from "@components/ReactivateRoleDialog/ReactivateRoleDialog.vue";
import RenameRoleDialog from "@components/RenameRoleDialog/RenameRoleDialog.vue";
import DeleteRoleDialog from "@components/DeleteRoleDialog/DeleteRoleDialog.vue";
import ArchivePopUp from "@components/common/ArchivePopUp.vue";
import UnarchiveNameConflictDialog from "@components/UnarchiveNameConflictDialog/UnarchiveNameConflictDialog.vue";
import { useUnarchiveOrgObject } from "@/composables/useUnarchiveOrgObject";
import GuideVideoBanner from "@components/GuideVideoBanner/GuideVideoBanner.vue";
import AttachedResponsibility from "@components/RolesCatalog/AttachedRole.vue";
import AttachRoleToPositionsInBulkDialog from "@components/AttachRoleToPositionsInBulkDialog/AttachRoleToPositionsInBulkDialog.vue";

/** Strip DRF/Python repr like [ErrorDetail(string="…", code='invalid')] to the inner message only. */
function humanizeErrorDetailMessage(text) {
  if (typeof text !== "string") return text;
  const dq = text.match(/ErrorDetail\(string="((?:[^"\\]|\\.)*)"/);
  if (dq) return dq[1].replace(/\\"/g, '"').replace(/\\\\/g, "\\");
  const sq = text.match(/ErrorDetail\(string='((?:[^'\\]|\\.)*)'/);
  if (sq) return sq[1].replace(/\\'/g, "'").replace(/\\\\/g, "\\");
  return text;
}

const { proxy } = getCurrentInstance();
const router = useRouter();

const emit = defineEmits(["drilldown", "guide-close"]);

// Props
const props = defineProps({
  guideData: {
    type: Object,
    default: null,
  },
  useNewBadge: {
    type: Boolean,
    default: false,
  },
  dataStream: {
    type: Array,
    default: null,
  },
  pagination: {
    type: Object,
    default: null,
  },
});

const rolesData = ref([]);
/**
 * ER-10902 — filter-aware indicator counts returned by the v2 role list.
 * Every field is optional; the UI falls back to 0 until the backend
 * (ER-10312 / ER-10903) ships the computed fields.
 */
const roleIndicators = ref({
  activeCount: 0,
  draftCount: 0,
  invalidCount: 0,
  noPositionAttachedCount: 0,
  notUsedCount: 0,
});
const paginationModel = ref({
  page: 1,
  total: 0,
  perPage: 10,
});
const isLoading = ref(false);
const detachLoader = ref(false);
const accountId = ref(null);
const currentFilters = ref(["draft", "active"]); // Default filters
const currentSearch = ref("");
const chatterData = ref([]);
const totalChatterData = ref(0);
const isInitialLoad = ref(true); // Track if initial load has happened

// Dialog states
const showActivateRoleDialog = ref(false);
const showActivateResponsibility = ref(false);
const skipActivateButtonLoader = ref(false);
const activateAttachedButtonLoader = ref(false);
const showRetireRoleDialog = ref(false);
const showReActivateRoleDialog = ref(false);
const activationEmployeeCount = ref(0);
const showRenameRoleDialog = ref(false);
const showDeleteRoleDialog = ref(false);
const showArchiveDialog = ref(false);
const archiveAction = ref(null);
const currentRole = ref(null);
const showAttachRoleToPositionsBulkDialog = ref(false);
const bulkAttachPositions = ref([]);
const isBulkAttachPositionsLoading = ref(false);
const bulkAttachTotalPositions = ref(0);
const bulkAttachQuery = ref({
  page: 1,
  perPage: 10,
  search: "",
  statuses: [],
});
const bulkAttachedPositionIds = ref(new Set());

// Attach responsibility to role state
const showAttachResponsibilityDialog = ref(false);
const attachableResponsibilities = ref([]);
const hasMoreResponsibilitiesData = ref(false);
const isLoadingMoreResponsibilities = ref(false);
const isAssigningResponsibility = ref(false);
const selectedResponsibilityId = ref(null);
const currentRoleId = ref(null);
const responsibilitiesPage = ref(1);
const responsibilitiesSearchTerm = ref("");
const existingResponsibilitiesNames = computed(() => {
  return attachableResponsibilities.value.map((item) => item.name);
});
const openDefaultAttachDialog = ref(false);
const renameRoleModel = computed(() => {
  const role = currentRole.value || {};
  return {
    id: role.id || role.data?.id || null,
    name: role.name || role.role_name || role.data?.name || "",
    context: role.context || role.data?.context || "",
    status: role.status || role.data?.status || "",
    archived: role.archived ?? role.data?.archived ?? false,
  };
});
const attachTargetRole = computed(() => {
  const role = currentRole.value;
  const id = currentRoleId.value;
  const name = String(role?.role_name || role?.data?.name || "").trim();
  const code = String(role?.role_code || role?.data?.code || "").trim().replace(/^#+/, "").split(/\s+/)[0];
  return id && name && code ? { type: "role", id, code, name } : null;
});
const draftResponsibilityCount = computed(() => {
  return currentRole.value?.info?.draft_responsibilities_count ?? 0;
});

// Convert frontend filters to API format (stable snake_case values + legacy labels)
const convertFiltersToAPIFormat = (filters) => convertRoleFiltersToApiFormat(filters);

// ER-11705: the Organization > Roles tab loads through this component-local
// function (not the Pinia roles store). Collapse concurrent identical loads and
// drop a superseded response so the newest filter always wins.
const loadRolesGuard = createLatestRequestGuard();

// Fetch roles from API
const loadRoles = async ({
  limit = 10,
  offset = 0,
  search = "",
  filters = {},
  ordering = "name",
  skipLoader = false,
} = {}) => {
  // If dataStream is provided via props (e.g., in Storybook), skip API calls
  if (props.dataStream !== null) {
    return;
  }

  let run;
  try {
    if (!skipLoader) {
      proxy.$loader.show();
    }
    isLoading.value = true;

    // Get account ID from localStorage
    if (!accountId.value) {
      accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
    }

    const effectiveOrdering = ordering === "data" ? "name" : ordering;
    const key = stableKey({
      accountId: accountId.value,
      limit,
      offset,
      search,
      filters,
      ordering: effectiveOrdering,
    });

    // Call the API directly, collapsing concurrent identical loads (ER-11705).
    run = loadRolesGuard.start(key, async (entry) => {
      const response = await getListOfRolesV2(
        accountId.value,
        limit,
        offset,
        search,
        filters,
        effectiveOrdering
      );

      // A newer request (different key) may have started while this one was in
      // flight. Discard this stale response so the newest filter always wins.
      if (!entry.isCurrent()) {
        return response;
      }

      const roleList = response?.data?.results || [];
      const totalCount = response?.data?.count || 0;

      // ER-10902 — filter-aware indicator counts returned by the v2 role list
      // (backend ER-10904, gated by idea-MDP-558). Every field is optional; the
      // UI falls back to 0 when the backend does not attach them.
      const toCount = (value) =>
        typeof value === "number" && Number.isFinite(value) ? value : 0;
      roleIndicators.value = {
        activeCount: toCount(response?.data?.active_count),
        draftCount: toCount(response?.data?.draft_count),
        invalidCount: toCount(response?.data?.invalid_count),
        noPositionAttachedCount: toCount(response?.data?.no_position_attached_count),
        notUsedCount: toCount(response?.data?.not_used_count),
      };

      // Transform the data to match the expected format for OrganizationRolesTable
      rolesData.value = roleList.map((el) => {
        return {
          id: el.id || null,
          role_code: el.code || "",
          role_name: el.name || "",
          status: el.status || "inactive",
          attached_position_count: el.position_attachment_count || 0,
          attached_res_count: el.responsibility_attachment_count || 0,
          // New badge component fields
          active_attached_position_count: el.active_attached_position_count || 0,
          inactive_attached_position_count: el.inactive_attached_position_count || 0,
          active_attached_responsibilities_count: el.active_attached_responsibilities_count || 0,
          inactive_attached_responsibilities_count: el.inactive_attached_responsibilities_count || 0,
          archived: el.archived || false,
          draft_responsibilities_count: el.draft_responsibilities_count || 0,
        };
      });

      // Update pagination
      paginationModel.value = {
        page: Math.floor(offset / limit) + 1,
        total: totalCount, // Total number of roles, not pages
        perPage: limit,
      };

      return response;
    });

    await run.promise;
  } catch (error) {
    console.error("Error loading roles:", error);
    if (!run || run.isCurrent()) {
      rolesData.value = [];
    }
  } finally {
    // Keep the reference-counted loader balanced; only a superseded caller
    // leaves `isLoading` true until the newer request finishes.
    if (!run || run.isCurrent()) {
      isLoading.value = false;
    }
    if (!skipLoader) {
      proxy.$loader.hide();
    }
  }
};

// Handle page change from table
const handlePageChange = async (pageData) => {
  const offset = (pageData.page - 1) * paginationModel.value.perPage;
  const apiFilters = convertFiltersToAPIFormat(currentFilters.value);
  await loadRoles({
    limit: paginationModel.value.perPage,
    offset: offset,
    search: currentSearch.value,
    filters: apiFilters,
    ordering: "name",
  });
};

// Handle filter change from table
const handleFilterChange = async (filters) => {
  currentFilters.value = filters;
  const apiFilters = convertFiltersToAPIFormat(filters);
  // Reset to page 1 when filters change
  await loadRoles({
    limit: paginationModel.value.perPage,
    offset: 0,
    search: currentSearch.value,
    filters: apiFilters,
    ordering: "name",
  });
  // Mark that initial load is complete
  isInitialLoad.value = false;
};

// Handle search change from table
const handleSearchChange = async (searchTerm) => {
  currentSearch.value = searchTerm;
  const apiFilters = convertFiltersToAPIFormat(currentFilters.value);
  // Reset to page 1 when search changes
  await loadRoles({
    limit: paginationModel.value.perPage,
    offset: 0,
    search: searchTerm,
    filters: apiFilters,
    ordering: "name",
  });
};

// Handle detach action from table
const handleDetach = async (detachData) => {
  try {
    // Note: The modal already sets $loader.setAppLoading(true) when detach is clicked
    detachLoader.value = true;
    const targetObj = detachData.target;
    const detachObj = detachData.detachObj;

    if (!accountId.value) {
      accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
    }

    if (targetObj.objectType.toLowerCase() === "role") {
      if (detachObj.type.toLowerCase() === "position") {
        // Detach role from position
        const payload = {
          childType: targetObj.objectType.toLowerCase(),
          childId: targetObj.objectId,
          parentData: {
            parent_id: detachObj.id,
          },
        };
        await detachOrgObject(accountId.value, payload);
      } else if (detachObj.type.toLowerCase() === "responsibility") {
        // Detach responsibility from role
        const payload = {
          childType: "responsibility",
          childId: detachObj.id,
          parentData: {
            parent_id: targetObj.objectId,
          },
        };
        await detachOrgObject(accountId.value, payload);
      }
    }

    // Reload the roles data to reflect the changes
    const apiFilters = convertFiltersToAPIFormat(currentFilters.value);
    await loadRoles({
      limit: paginationModel.value.perPage,
      offset: (paginationModel.value.page - 1) * paginationModel.value.perPage,
      search: currentSearch.value,
      filters: apiFilters,
      ordering: "name",
    });

    proxy.$notifications.success("Detached successfully.");

    // Call the callback function to close the modal AFTER reloading data
    if (detachData.callBackFunc) {
      detachData.callBackFunc();
    }
  } catch (error) {
    console.error("Error detaching:", error);
    const data = error?.response?.data;
    let msg = "Unable to detach. Please try again.";
    if (data != null) {
      if (typeof data === "string") {
        msg = data;
      } else if (Array.isArray(data) && data.length) {
        msg = String(data[0]);
      } else if (data.detail != null) {
        msg = typeof data.detail === "string" ? data.detail : String(data.detail);
      } else if (data.message != null) {
        msg = typeof data.message === "string" ? data.message : String(data.message);
      }
    } else if (error?.message) {
      msg = error.message;
    }
    proxy.$notifications.error(humanizeErrorDetailMessage(msg));
  } finally {
    // Turn off the loader to re-enable buttons
    proxy.$loader.setAppLoading(false);
    detachLoader.value = false;
  }
};

// Handle opening chatter drawer
const handleOpenChatterDrawer = async (roleData) => {
  try {
    // Reset chatter data
    chatterData.value = [];
    totalChatterData.value = 0;

    if (!accountId.value) {
      accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
    }

    const roleId = roleData.id;
    const page = 1;

    const { data } = await getChatterList(accountId.value, roleId, "role", page);

    if (data && data.count) {
      totalChatterData.value = data.count;
    }

    if (data && data.results) {
      const chatterItems = data.results.map((el) => {
        const date = new Date(el.recorded_on);
        const milliseconds = date.getTime();
        const millisecondsString = milliseconds.toString();
        const nameParts = el.recorded_by_name.split(" ");

        const newEl = {
          id: el.id,
          avatar:
            nameParts.length === 2
              ? {
                  picture: "",
                  firstName: nameParts[0],
                  lastName: nameParts[1],
                }
              : {
                  picture: "",
                  firstName: el.recorded_by_name,
                  lastName: "",
                },
          IsExIQtiveBot: false,
          message: el.note,
          created_on: millisecondsString,
          IsHistory: el.is_history,
        };

        return newEl;
      });

      chatterData.value.push(...chatterItems);
    }
  } catch (error) {
    console.error("Error loading chatter data:", error);
  }
};

// Handle loading more chatter messages
const handleRequestMoreChatter = async (roleData) => {
  try {
    if (!accountId.value) {
      accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
    }

    const roleId = roleData.id;
    const currentLength = chatterData.value.length;
    const page = Math.ceil(currentLength / 10) + 1;

    const { data } = await getChatterList(accountId.value, roleId, "role", page);

    if (data && data.results) {
      const chatterItems = data.results.map((el) => {
        const date = new Date(el.recorded_on);
        const milliseconds = date.getTime();
        const millisecondsString = milliseconds.toString();
        const nameParts = el.recorded_by_name.split(" ");

        const newEl = {
          id: el.id,
          avatar:
            nameParts.length === 2
              ? {
                  picture: "",
                  firstName: nameParts[0],
                  lastName: nameParts[1],
                }
              : {
                  picture: "",
                  firstName: el.recorded_by_name,
                  lastName: "",
                },
          IsExIQtiveBot: false,
          message: el.note,
          created_on: millisecondsString,
          IsHistory: el.is_history,
        };

        return newEl;
      });

      chatterData.value.push(...chatterItems);
    }
  } catch (error) {
    console.error("Error loading more chatter data:", error);
  }
};

// Handle dropdown menu operations
const handleRowOperation = async (operation) => {
  const action = operation.item?.value;
  currentRole.value = operation.data;

  switch (action) {
    case "activate_role":
      await openActivationConfirmation("activate");
      break;
    case "retired_role":
      showRetireRoleDialog.value = true;
      break;
    case "re_activate_role":
      await openActivationConfirmation("reactivate");
      break;
    case "attach_responsibility_role":
      currentRoleId.value = currentRole.value?.id || currentRole.value?.data?.id;
      openDefaultAttachDialog.value = true;
      showAttachResponsibilityDialog.value = true;
      responsibilitiesPage.value = 1;
      responsibilitiesSearchTerm.value = "";
      await fetchAttachableResponsibilitiesList();
      break;
    case "attach_role_positions_bulk":
      await openAttachRoleToPositionsBulkDialog();
      break;
    case "rename_role":
      showRenameRoleDialog.value = true;
      break;
    case "delete_role":
      showDeleteRoleDialog.value = true;
      break;
    case "archive_action":
      archiveAction.value = "archive";
      showArchiveDialog.value = true;
      break;
    case "unarchive_action":
      archiveAction.value = "unarchive";
      await unarchiveRole.start({
        id: operation.data?.id || operation.data?.data?.id,
        name: operation.data?.role_name || operation.data?.data?.name || operation.data?.name || "",
      });
      break;
    default:
      console.log("Unknown action:", action);
  }
};

const handleRoleDrillDown = (roleData) => {
  // Navigate to role detail route using router
  if (proxy?.$router && roleData.id) {
    proxy.$router
      .push({
        name: "RoleDetailChild",
        params: { roleId: roleData.id },
        query: { roleTab: "attached_responsibilities" },
      })
      .catch(() => {});
  }
};

// ER-11818 / Roles Rules 3.4 & 3.6 — preview who will be assigned before activating or
// reactivating a Role. N > 0 opens the confirmation dialog with the count; N = 0 proceeds
// directly; an undeterminable preview falls back to the pre-existing dialog.
const openActivationConfirmation = async (kind) => {
  const idRole = currentRole.value?.id || currentRole.value?.data?.id;
  if (!accountId.value) {
    accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
  }
  const count = await fetchRoleActivationCount(kind, accountId.value, idRole);
  activationEmployeeCount.value = count ?? 0;
  if (count === 0) {
    await (kind === "activate" ? onActivateRoleConfirm() : reActivateRole());
    return;
  }
  if (kind === "activate") showActivateRoleDialog.value = true;
  else showReActivateRoleDialog.value = true;
};

// Dialog action handlers: when user confirms Activate in ActivateRoleDialog
const onActivateRoleConfirm = () => {
  const draftCount = draftResponsibilityCount.value;
  showActivateRoleDialog.value = false;
  if (draftCount > 0) {
    showActivateResponsibility.value = true;
    return;
  }
  processRoleActivation(false);
};

const processRoleActivation = async (activateResponsibilities = false) => {
  try {
    proxy.$loader.setAppLoading(true);
    if (activateResponsibilities) {
      activateAttachedButtonLoader.value = true;
    } else {
      skipActivateButtonLoader.value = true;
    }
    if (!accountId.value) {
      accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
    }
    const idRole = currentRole.value?.id || currentRole.value?.data?.id;
    const payload = {
      account_id: accountId.value,
      role_id: idRole,
      ...(activateResponsibilities && { activate_responsibilities: true }),
    };
    await updateActiveRole(accountId.value, idRole, payload);
    showActivateResponsibility.value = false;
    currentRole.value = null;
    await reloadCurrentPage();
  } catch (error) {
    console.error("Error activating role:", error);
    showActivateResponsibility.value = false;
  } finally {
    proxy.$loader.setAppLoading(false);
    skipActivateButtonLoader.value = false;
    activateAttachedButtonLoader.value = false;
  }
};

const skipActivateResponsibility = () => {
  processRoleActivation(false);
};

const activateAttachedResponsibilities = () => {
  processRoleActivation(true);
};

const retireRole = async () => {
  try {
    proxy.$loader.setAppLoading(true);
    const idRole = currentRole.value?.id || currentRole.value?.data?.id;
    const nameRole = currentRole.value?.data?.name;
    const payload = {
      name: nameRole,
      status: "retired",
      account: accountId.value,
    };
    await updateRetiredRole(accountId.value, idRole, payload);
    showRetireRoleDialog.value = false;
    currentRole.value = null;
    // Reload data
    await reloadCurrentPage();
  } catch (error) {
    console.error("Error retiring role:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const reActivateRole = async () => {
  try {
    proxy.$loader.setAppLoading(true);
    const idRole = currentRole.value?.id || currentRole.value?.data?.id;
    const nameRole = currentRole.value?.data?.name;
    const payload = {
      name: nameRole,
      status: "inactive",
      account: accountId.value,
    };
    await updateReActiveRole(accountId.value, idRole, payload);
    showReActivateRoleDialog.value = false;
    currentRole.value = null;
    // Reload data
    await reloadCurrentPage();
  } catch (error) {
    console.error("Error re-activating role:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const onRoleRenamed = async () => {
  try {
    proxy.$loader.setAppLoading(true);
    showRenameRoleDialog.value = false;
    currentRole.value = null;
    // Reload data
    await reloadCurrentPage();
  } catch (error) {
    console.error("Error refreshing roles after rename:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const deleteRole = async (deleteDraftResponsibilities = false) => {
  try {
    proxy.$loader.setAppLoading(true);
    const idRole = currentRole.value?.id || currentRole.value?.data?.id;
    const payload = {
      delete_draft_responsibilities: deleteDraftResponsibilities,
    };

    if (deleteDraftResponsibilities) {
      await deleteReadinessRoleWithResponsibilities(accountId.value, idRole, payload);
    } else {
      await deleteReadinessRole(accountId.value, idRole);
    }

    showDeleteRoleDialog.value = false;
    currentRole.value = null;
    // Reload data
    await reloadCurrentPage();
  } catch (error) {
    console.error("Error deleting role:", error);
    proxy.$notifications.error(getDeleteErrorMessage(error, { entity: "role" }));
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const unarchiveRole = useUnarchiveOrgObject("role", {
  getAccountId: () => accountId.value,
  openConfirm: () => {
    showArchiveDialog.value = true;
  },
  closeConfirm: () => {
    showArchiveDialog.value = false;
  },
  onUnarchived: async () => {
    currentRole.value = null;
    await reloadCurrentPage();
  },
  setLoading: (value) => proxy.$loader.setAppLoading(value),
});
const {
  target: unarchiveTarget,
  conflictOpen: unarchiveConflictOpen,
  busy: unarchiveBusy,
  errorMessage: unarchiveError,
  nameError: unarchiveNameError,
} = unarchiveRole;

const onArchiveDialogClose = () => {
  showArchiveDialog.value = false;
  unarchiveRole.close();
};

const onArchiveRole = async () => {
  if (archiveAction.value === "unarchive") {
    await unarchiveRole.confirm();
    return;
  }
  try {
    proxy.$loader.setAppLoading(true);
    const idRole = currentRole.value?.id || currentRole.value?.data?.id;
    const objType = "role";

    await updateArchiveRole(accountId.value, objType, idRole);

    showArchiveDialog.value = false;
    currentRole.value = null;
    // Reload data
    await reloadCurrentPage();
  } catch (error) {
    console.error("Error archiving/unarchiving role:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

// Helper to reload current page
const reloadCurrentPage = async () => {
  const apiFilters = convertFiltersToAPIFormat(currentFilters.value);
  await loadRoles({
    limit: paginationModel.value.perPage,
    offset: (paginationModel.value.page - 1) * paginationModel.value.perPage,
    search: currentSearch.value,
    filters: apiFilters,
    ordering: "name",
  });
};

// Attach responsibility to role methods
const fetchAttachableResponsibilitiesList = async (isLoadMore = false) => {
  if (!currentRoleId.value || !accountId.value) return;

  const limit = 10;
  const offset = (responsibilitiesPage.value - 1) * limit;
  const name = responsibilitiesSearchTerm.value || "";

  try {
    if (!isLoadMore) {
      attachableResponsibilities.value = [];
    }
    isLoadingMoreResponsibilities.value = true;

    const response = await fetchAttachableResponsibilities(
      accountId.value,
      currentRoleId.value,
      limit,
      offset,
      name
    );

    const newItems = response.data.results.map((item) => ({
      id: item.id,
      code: item.code_str?.match(/\d+/)?.[0] || "",
      name: item.name,
      status: item.status,
      attached: item?.attached_to_role,
    }));

    if (isLoadMore && attachableResponsibilities.value) {
      attachableResponsibilities.value = [...attachableResponsibilities.value, ...newItems];
    } else {
      attachableResponsibilities.value = newItems;
    }

    const totalCount = response.data.count || 0;
    hasMoreResponsibilitiesData.value = responsibilitiesPage.value * limit < totalCount;
  } catch (error) {
    console.error("Error fetching attachable responsibilities:", error);
  } finally {
    isLoadingMoreResponsibilities.value = false;
  }
};

const handleResponsibilitySelected = (responsibility) => {
  if (responsibility && Object.prototype.hasOwnProperty.call(responsibility, "id")) {
    selectedResponsibilityId.value = responsibility.id;
  }
};

const handleAddNewResponsibility = async (item) => {
  try {
    proxy.$loader.show();
    const payload = {
      name: item.name,
      accountId: accountId.value,
    };
    const newResponsibility = await proxy.$store.dispatch(
      "readinessCatalogsStore/createResponsibility",
      payload
    );
    selectedResponsibilityId.value = newResponsibility.data.id;
    proxy.$loader.hide();
    // handleAttachResponsibilityToRole will manage its own loader and close the dialog
    await handleAttachResponsibilityToRole();
  } catch (error) {
    console.error("Error creating responsibility:", error);
    proxy.$loader.hide();
  }
};

const handleAttachResponsibilityToRole = async () => {
  if (!selectedResponsibilityId.value || !currentRoleId.value) return;

  try {
    isAssigningResponsibility.value = true;
    proxy.$loader.setAppLoading(true);

    const payload = {
      attachment_id: selectedResponsibilityId.value,
    };

    await attachResponsibilityToRole(accountId.value, currentRoleId.value, payload);

    showAttachResponsibilityDialog.value = false;
    selectedResponsibilityId.value = null;
    responsibilitiesPage.value = 1;
    responsibilitiesSearchTerm.value = "";

    // Reload roles data - skip loader since we're already managing it
    const apiFilters = convertFiltersToAPIFormat(currentFilters.value);
    await loadRoles({
      limit: paginationModel.value.perPage,
      offset: (paginationModel.value.page - 1) * paginationModel.value.perPage,
      search: currentSearch.value,
      filters: apiFilters,
      ordering: "name",
      skipLoader: true,
    });
  } catch (error) {
    console.error("Error attaching responsibility:", error);
  } finally {
    isAssigningResponsibility.value = false;
    proxy.$loader.setAppLoading(false);
  }
};

// AddAndAttachResponsibilityDialog already created and attached the Responsibility; just refresh.
const handleResponsibilityCreatedAndAttached = async () => {
  closeAttachResponsibilityDialog();
  const apiFilters = convertFiltersToAPIFormat(currentFilters.value);
  await loadRoles({
    limit: paginationModel.value.perPage,
    offset: (paginationModel.value.page - 1) * paginationModel.value.perPage,
    search: currentSearch.value,
    filters: apiFilters,
    ordering: "name",
  });
};

const loadMoreResponsibilities = async () => {
  if (isLoadingMoreResponsibilities.value || !hasMoreResponsibilitiesData.value) {
    return;
  }
  responsibilitiesPage.value += 1;
  await fetchAttachableResponsibilitiesList(true);
};

const searchResponsibilities = async (searchTerm) => {
  responsibilitiesSearchTerm.value = searchTerm;
  responsibilitiesPage.value = 1;
  await fetchAttachableResponsibilitiesList(false);
};

const closeAttachResponsibilityDialog = () => {
  showAttachResponsibilityDialog.value = false;
  openDefaultAttachDialog.value = false;
  selectedResponsibilityId.value = null;
  responsibilitiesPage.value = 1;
  responsibilitiesSearchTerm.value = "";
};

const loadAttachedPositionIdsForRole = async (roleId) => {
  const ids = new Set();
  const limit = 100;
  let offset = 0;
  let count = 0;
  do {
    const response = await getListOfPositionsAttachedToRole(
      accountId.value,
      roleId,
      limit,
      offset,
      "",
      {}
    );
    const results = response?.data?.results || [];
    count = response?.data?.count || 0;
    results.forEach((item) => ids.add(item.id));
    offset += limit;
  } while (offset < count);
  return ids;
};

const loadBulkAttachPositionsPage = async ({
  page = bulkAttachQuery.value.page,
  perPage = bulkAttachQuery.value.perPage,
  search = bulkAttachQuery.value.search,
  statuses = bulkAttachQuery.value.statuses,
} = {}) => {
  const offset = (page - 1) * perPage;
  const queryParamsObj = {
    limit: perPage,
    offset,
    archived: false,
    ordering: "name",
    name: search || undefined,
  };
  if (statuses && statuses.length > 0) {
    queryParamsObj.status = statuses;
  }
  const response = await getListOfPositionsV2({
    account_id: accountId.value,
    queryParamsObj,
  });
  const results = response?.data?.results || [];
  const totalCount = response?.data?.count || 0;
  bulkAttachTotalPositions.value = totalCount;
  bulkAttachPositions.value = results.map((position) => ({
    id: position.id,
    code:
      position.code ??
      String(position.code_str || "")
        .replace("#", "")
        .split(" ")[0] ??
      "",
    name: position.name,
    status: position.status || "draft",
    attached: bulkAttachedPositionIds.value.has(position.id),
    // Show attached role count in PositionCard (same as positions column behavior)
    attachmentsCount: position.attachments_count?.role ?? position.roles_count ?? 0,
    rolesCount: position.attachments_count?.role ?? position.roles_count ?? 0,
    responsibilitiesCount:
      position.attachments_count?.responsibility ?? position.responsibilities_count ?? 0,
  }));
};

const handleBulkAttachPositionRequest = async (requestPayload = {}) => {
  if (!showAttachRoleToPositionsBulkDialog.value) return;
  bulkAttachQuery.value = {
    ...bulkAttachQuery.value,
    ...requestPayload,
  };
  isBulkAttachPositionsLoading.value = true;
  try {
    await loadBulkAttachPositionsPage(bulkAttachQuery.value);
  } catch (error) {
    console.error("Error loading bulk attach positions page:", error);
  } finally {
    isBulkAttachPositionsLoading.value = false;
  }
};

const openAttachRoleToPositionsBulkDialog = async () => {
  const roleId = currentRole.value?.id || currentRole.value?.data?.id;
  if (!roleId || !accountId.value) return;
  showAttachRoleToPositionsBulkDialog.value = true;
  bulkAttachQuery.value = {
    page: 1,
    perPage: 10,
    search: "",
    statuses: [],
  };
  bulkAttachedPositionIds.value = new Set();
  bulkAttachTotalPositions.value = 0;
  bulkAttachPositions.value = [];
  isBulkAttachPositionsLoading.value = true;
  try {
    // Load attached IDs asynchronously so first render is fast.
    loadAttachedPositionIdsForRole(roleId)
      .then((ids) => {
        bulkAttachedPositionIds.value = ids;
        bulkAttachPositions.value = bulkAttachPositions.value.map((position) => ({
          ...position,
          attached: ids.has(position.id),
        }));
      })
      .catch((error) => {
        console.error("Error loading attached position IDs:", error);
      });
    await loadBulkAttachPositionsPage(bulkAttachQuery.value);
  } catch (error) {
    console.error("Error opening bulk attach dialog:", error);
  } finally {
    isBulkAttachPositionsLoading.value = false;
  }
};

const handleBulkAttachRoleToPositions = async (payload) => {
  const positionIds = payload?.positionIds || [];
  const roleId = currentRole.value?.id || currentRole.value?.data?.id;
  if (!roleId || positionIds.length === 0) return;
  proxy.$loader.setAppLoading(true);
  try {
    await Promise.all(
      positionIds.map((positionId) =>
        attachRoleToPosition(accountId.value, positionId, {
          attachment_id: roleId,
        })
      )
    );
    proxy.$notifications.success("Role attached to selected positions Successfully.");
    showAttachRoleToPositionsBulkDialog.value = false;
    await reloadCurrentPage();
  } catch (error) {
    console.error("Error attaching role to positions in bulk:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const closeAttachRoleToPositionsBulkDialog = () => {
  showAttachRoleToPositionsBulkDialog.value = false;
  isBulkAttachPositionsLoading.value = false;
  bulkAttachTotalPositions.value = 0;
  bulkAttachedPositionIds.value = new Set();
  bulkAttachPositions.value = [];
};

const onBulkAttachAddPosition = () => {
  closeAttachRoleToPositionsBulkDialog();
  router.push({ name: "NewOrganization", params: { tab: "Positions" } }).catch(() => {});
};

// Computed property for feature flag
const showAttachResponsibilitiesPicker = computed(() => {
  return proxy.$store.getters["featureFlags/ideaMDP389"];
});

// Initialize data on component mount
onMounted(async () => {
  // If dataStream is provided (e.g., from Storybook), use it directly
  if (props.dataStream !== null) {
    rolesData.value = props.dataStream;
    if (props.pagination) {
      paginationModel.value = props.pagination;
    }
    isLoading.value = false;
    isInitialLoad.value = false;
    return;
  }

  // Initialize accountId
  if (!accountId.value) {
    accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
  }

  // Wait for nextTick to ensure child component has mounted and emitted initial filters
  // The child component emits filters in its mounted() hook via applySavedFilters()
  // (ER-11193: mounted, not created — created races the Access context after login)
  await proxy.$nextTick();

  // Use a small delay to ensure child component's applySavedFilters() has completed
  // and emitted the filters via onFilterChange event before we check if we need to load
  await new Promise((resolve) => setTimeout(resolve, 50));

  // Only load if initial load hasn't happened yet (child hasn't emitted filters)
  // This ensures we don't double-load: either child emits filters and loads, or we load with defaults
  if (isInitialLoad.value) {
    // If child didn't emit filters (shouldn't happen, but fallback), use default filters
    const apiFilters = convertFiltersToAPIFormat(currentFilters.value);
    await loadRoles({
      limit: 10,
      offset: 0,
      search: "",
      filters: apiFilters,
      ordering: "name",
    });
    isInitialLoad.value = false;
  }
});

// Expose methods for parent components to use
defineExpose({
  loadRoles,
  rolesData,
  paginationModel,
  isLoading,
});
</script>

<style>
.org-role-tab {
  margin-top: 15px;
}
</style>

<style scoped>
.guide-video-banner {
  margin-top: 15px;
  margin-bottom: 15px;
}
</style>
